import { useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import LayoutContainer from 'components/Common/LayoutContainer'
import BodyText from 'LuxKit/Typography/BodyText'
import config from 'constants/config'
import Logo from 'components/App/Header/Logo'
import { mediaQueryUp } from 'components/utils/breakpoint'
import getStaticPagesByType from 'lib/utils/getStaticPagesByType'
import TextLink from 'LuxKit/TextLink'
import CookieSettingsLink from 'components/Consent/CookieSettingsLink'
import { useHeaderStateObserver } from 'contexts/HeaderStateContext'
import Group from 'components/utils/Group'
import { useLocalizedConfigLabels } from 'hooks/useLocalizedConfigLabels'

const Root = styled.div`
  border-top: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  padding-top: ${rem(40)};
  padding-bottom: ${rem(40)};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};

  ${mediaQueryUp.tablet} {
    padding-top: ${rem(20)};
    padding-bottom: ${rem(20)};
  }

  &.mobile-app-like-checkout-footer-offset {
    padding-bottom: ${rem(142)};

    ${mediaQueryUp.desktop} {
      padding-bottom: ${rem(20)};
    }
  }
`

const Content = styled(LayoutContainer)`
  @media print {
    a[href]::after {
      content: none !important;
    }
  }
`

const FooterLogo = styled(Logo)`
  width: ${rem(144)};
  height: unset;
`

interface Props {
  className?: string
}

function MinimalFooter({ className }: Props) {
  const intl = useIntl()
  const currentYear = new Date().getFullYear()

  const pageTypes = ['privacyPolicy', 'termsAndConditions', 'bookingPolicies']

  const helpItems = getStaticPagesByType(pageTypes, true)

  const configLabels = useMemo(
    () => helpItems.flatMap(item => typeof item.text === 'string' ? [item.text] : []),
    [helpItems],
  )
  const resolveConfigLabel = useLocalizedConfigLabels(configLabels)
  const localizedLinkText = (raw: string) => resolveConfigLabel(raw) ?? raw

  const footerDiv = useRef<HTMLDivElement>(null)
  useHeaderStateObserver('tripPlannerFooter', footerDiv)

  return <Root ref={footerDiv} className={className}>
    <Content size="xl">
      <Group
        direction="vertical"
        desktopDirection="horizontal"
        horizontalAlign="center"
        desktopHorizontalAlign="start"
        verticalAlign="center"
        gap={40}
      >
        <FooterLogo />
        <Group direction="vertical" desktopDirection="horizontal" desktopHorizontalAlign="space-between" gap={8}>
          <BodyText variant="medium">
            {intl.formatMessage(
              {
                id: '4fcdDrLw5ZJLsrJc4OeJ',
                defaultMessage: 'Copyright © 2017 - {year} {title}.',
                description: 'Copyright notice in footer with year and company title',
              },
              {
                year: currentYear,
                title: config.title,
              },
            )}
          </BodyText>
          <Group direction="horizontal" gap={12} wrap="wrap" horizontalAlign="center">
            {helpItems.map(item => <TextLink
              key={item.type}
              size="medium"
              weight="regular"
              to={item.url?.to}
              href={item.url?.href}
              target="_blank"
            >
              {typeof item.text === 'string'
                ? localizedLinkText(item.text)
                : intl.formatMessage(item.text)}
            </TextLink>)}
            <CookieSettingsLink variant="inline" />
          </Group>
        </Group>
      </Group>
    </Content>
  </Root>
}

export default MinimalFooter
